import { useMemo, useState } from 'react';
import type { GeoBrowseGeneStudy } from '../../types';
import { formatDiseaseName } from '../../utils/formatters';
import { comparisonRowKey, isWholeStudyComparison } from '../../utils/studyHelpers';
import styles from '../../GeoBrowse.module.css';

const CI_MULT = 1.96;
const SIG_COLOR = '#8B1538';
const NS_COLOR = '#bdbdbd';
const ROW_HEIGHT = 20;
const PANEL_MAX_HEIGHT = 480;

type GeneEffectForestPlotProps = {
  panels: { geneSymbol: string; rows: GeoBrowseGeneStudy[] }[];
  allSelectedGenes: string[];
  viewingGenes: string[];
  onViewingGenesChange: (genes: string[]) => void;
};

type ForestPanelProps = {
  geneSymbol: string;
  rows: GeoBrowseGeneStudy[];
  sharedExtent: { min: number; max: number };
  showLabels: boolean;
  fullWidth: boolean;
};

function studyLabel(row: GeoBrowseGeneStudy): string {
  const disease = formatDiseaseName(row.cui_name);
  if (isWholeStudyComparison(row)) return `${disease} (${row.accession})`;
  return `${disease} (${row.accession} · ${row.comparison_id})`;
}

function ForestPanel({ geneSymbol, rows, sharedExtent, showLabels, fullWidth }: ForestPanelProps) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.log2FoldChange - b.log2FoldChange),
    [rows],
  );

  const labelWidth = showLabels ? (fullWidth ? 280 : 210) : 16;
  const plotWidth = fullWidth ? 420 : 260;
  const margin = { top: 12, right: 16, bottom: 36, left: labelWidth };
  const height = sorted.length * ROW_HEIGHT + margin.top + margin.bottom;

  const xScale = (value: number) =>
    margin.left + ((value - sharedExtent.min) / (sharedExtent.max - sharedExtent.min)) * plotWidth;

  const zeroX = xScale(0);
  const ticks = useMemo(() => {
    const span = sharedExtent.max - sharedExtent.min;
    const step = span <= 4 ? 1 : span <= 8 ? 2 : 4;
    const values: number[] = [];
    const start = Math.ceil(sharedExtent.min / step) * step;
    for (let v = start; v <= sharedExtent.max; v += step) {
      values.push(Number(v.toFixed(2)));
    }
    return values;
  }, [sharedExtent]);

  return (
    <div className={styles.forestPanel}>
      <h4 className={styles.forestPanelTitle}>{geneSymbol}</h4>
      <div
        className={styles.forestPanelScroll}
        style={{ maxHeight: PANEL_MAX_HEIGHT }}
      >
        <svg
          className={styles.forestSvg}
          width={margin.left + plotWidth + margin.right}
          height={height}
          role="img"
          aria-label={`Effect size forest plot for ${geneSymbol}`}
        >
          <line
            x1={zeroX}
            x2={zeroX}
            y1={margin.top - 4}
            y2={height - margin.bottom}
            stroke="#94a3b8"
            strokeWidth={1}
            strokeDasharray="4 4"
          />

          {sorted.map((row, index) => {
            const y = margin.top + index * ROW_HEIGHT + ROW_HEIGHT / 2;
            const lo = row.log2FoldChange - CI_MULT * row.lfcSE;
            const hi = row.log2FoldChange + CI_MULT * row.lfcSE;
            const color = row.significant ? SIG_COLOR : NS_COLOR;
            const label = studyLabel(row);

            return (
              <g key={comparisonRowKey(row)}>
                {showLabels ? (
                  <text
                    x={margin.left - 8}
                    y={y}
                    textAnchor="end"
                    dominantBaseline="central"
                    fontSize={9}
                    fill="#374151"
                  >
                    <title>{label}</title>
                    {label.length > (fullWidth ? 48 : 38)
                      ? `${label.slice(0, fullWidth ? 47 : 37)}…`
                      : label}
                  </text>
                ) : null}
                <line
                  x1={xScale(lo)}
                  x2={xScale(hi)}
                  y1={y}
                  y2={y}
                  stroke={color}
                  strokeWidth={1.5}
                  opacity={0.85}
                >
                  <title>{label}</title>
                </line>
                <circle cx={xScale(row.log2FoldChange)} cy={y} r={3.5} fill={color}>
                  <title>{label}</title>
                </circle>
              </g>
            );
          })}

          <g transform={`translate(0, ${height - margin.bottom + 18})`}>
            {ticks.map(tick => (
              <text
                key={tick}
                x={xScale(tick)}
                y={0}
                textAnchor="middle"
                fontSize={9}
                fill="#6b7280"
              >
                {tick}
              </text>
            ))}
            <text
              x={margin.left + plotWidth / 2}
              y={18}
              textAnchor="middle"
              fontSize={10}
              fill="#374151"
              fontWeight={500}
            >
              log2FC
            </text>
          </g>
        </svg>
      </div>
    </div>
  );
}

export default function GeneEffectForestPlot({
  panels,
  allSelectedGenes,
  viewingGenes,
  onViewingGenesChange,
}: GeneEffectForestPlotProps) {
  const [showLabels, setShowLabels] = useState(true);

  const sharedExtent = useMemo(() => {
    let min = 0;
    let max = 0;

    panels.forEach(panel => {
      panel.rows.forEach(row => {
        const lo = row.log2FoldChange - CI_MULT * row.lfcSE;
        const hi = row.log2FoldChange + CI_MULT * row.lfcSE;
        min = Math.min(min, lo);
        max = Math.max(max, hi);
      });
    });

    const pad = Math.max((max - min) * 0.08, 0.35);
    return { min: min - pad, max: max + pad };
  }, [panels]);

  const toggleViewingGene = (symbol: string) => {
    if (viewingGenes.includes(symbol)) {
      if (viewingGenes.length > 1) {
        onViewingGenesChange(viewingGenes.filter(g => g !== symbol));
      }
      return;
    }

    if (viewingGenes.length < 2) {
      onViewingGenesChange([...viewingGenes, symbol]);
      return;
    }

    onViewingGenesChange([viewingGenes[1], symbol]);
  };

  if (!panels.length) {
    return <div className={styles.tabEmpty}>Select at least one gene to plot.</div>;
  }

  const gridColumns = panels.length === 1 ? '1fr' : 'repeat(2, minmax(320px, 1fr))';

  return (
    <div className={styles.forestPlotWrap}>
      <div className={styles.forestPlotHeader}>
        <div>
          <h3 className={styles.forestPlotHeading}>
            Effect size (log2FC) across studies
          </h3>
          <p className={styles.forestPlotHint}>
            log2FoldChange (95% CI from lfcSE) — unlike baseMean, this IS comparable across studies:
            it&apos;s a within-study case-vs-control ratio, not a raw normalized count. Dotted line at 0 =
            no change.
          </p>
        </div>
        <div className={styles.forestLegendHtml} aria-label="Significance legend">
          <span className={styles.forestLegendItem}>
            <span className={styles.forestLegendDot} style={{ background: NS_COLOR }} />
            FALSE
          </span>
          <span className={styles.forestLegendItem}>
            <span className={styles.forestLegendDot} style={{ background: SIG_COLOR }} />
            TRUE
          </span>
        </div>
      </div>

      {allSelectedGenes.length > 2 && (
        <p className={styles.forestViewHint}>
          {allSelectedGenes.length} genes selected — forest plot shows up to 2 at a time. Use the Table
          tab to browse all {allSelectedGenes.length} genes.
        </p>
      )}

      <div className={styles.forestViewToolbar}>
        <div className={styles.forestViewPills} role="group" aria-label="Genes shown in forest plot">
          <span className={styles.forestViewPillsLabel}>Viewing:</span>
          {allSelectedGenes.map(symbol => {
            const isActive = viewingGenes.includes(symbol);
            return (
              <button
                key={symbol}
                type="button"
                className={`${styles.forestViewPill} ${isActive ? styles.forestViewPillActive : ''}`}
                aria-pressed={isActive}
                onClick={() => toggleViewingGene(symbol)}
              >
                {symbol}
              </button>
            );
          })}
        </div>
        <label className={styles.forestToggle}>
          <input
            type="checkbox"
            checked={showLabels}
            onChange={e => setShowLabels(e.target.checked)}
          />
          Show study labels
        </label>
      </div>

      <div className={styles.forestPlotGrid} style={{ gridTemplateColumns: gridColumns }}>
        {panels.map(panel => (
          <ForestPanel
            key={panel.geneSymbol}
            geneSymbol={panel.geneSymbol}
            rows={panel.rows}
            sharedExtent={sharedExtent}
            showLabels={showLabels}
            fullWidth={panels.length === 1}
          />
        ))}
      </div>
    </div>
  );
}
